/**
 * Single source of truth for CMS image upload guidance + soft validation.
 * Badges show ratioLabel + maxWeightKey only.
 * Ticket (Quentin/Julien): recommended ratios / formats — not min pixel size.
 */
export type ImageWeightKey = "imageMaxSize200kb" | "imageMaxSize300kb" | "imageMaxSize1Mb" | "imageMaxSize4Mb";

export interface ImageSpec {
    /** Label shown in the Ratio badge. Null = free format (no ratio badge). */
    ratioLabel: string | null;
    /** Numeric ratio for soft validation. Null = skip ratio check. */
    ratio: number | null;
    /** i18n key for the Weight badge */
    maxWeightKey: ImageWeightKey;
    /** Alternate accepted ratios (e.g. cover + gallery in one list) */
    acceptedRatios?: readonly number[];
    /** Override default 0.1 tolerance */
    ratioTolerance?: number;
}

export const IMAGE_SPECS = {
    productCover: {
        ratioLabel: "2.27:1",
        ratio: 2.27,
        maxWeightKey: "imageMaxSize200kb",
    },
    productGallery: {
        ratioLabel: "≈ 1.5:1",
        ratio: 1.5,
        maxWeightKey: "imageMaxSize200kb",
    },
    /** Waypoint / story pictures — visit detail BottomSheet & Art VC are 1:1 */
    waypoint: {
        ratioLabel: "1:1",
        ratio: 1,
        maxWeightKey: "imageMaxSize200kb",
    },
    /** Track Basic Photos — recommend cover ratio; gallery ≈1.5 still accepted softly */
    productPhotos: {
        ratioLabel: "2.27:1",
        ratio: 2.27,
        maxWeightKey: "imageMaxSize200kb",
        acceptedRatios: [2.27, 1.5],
    },
    activity: {
        ratioLabel: "2.27:1",
        ratio: 2.27,
        maxWeightKey: "imageMaxSize200kb",
    },
    /** Hot Info / warmInfo — detail carousel 1.5:1 (list card 2.27 is secondary) */
    hotInfo: {
        ratioLabel: "1.5:1",
        ratio: 1.5,
        maxWeightKey: "imageMaxSize200kb",
    },
    quiz: {
        ratioLabel: "16:9",
        ratio: 16 / 9,
        maxWeightKey: "imageMaxSize200kb",
    },
    square: {
        ratioLabel: "1:1",
        ratio: 1,
        maxWeightKey: "imageMaxSize200kb",
    },
    notificationLogo: {
        ratioLabel: "1:1",
        ratio: 1,
        maxWeightKey: "imageMaxSize200kb",
    },
    museumLogo: {
        ratioLabel: "≈ 1.2:1",
        ratio: 1.17,
        maxWeightKey: "imageMaxSize200kb",
        ratioTolerance: 0.25,
    },
    artwork: {
        ratioLabel: "1:1",
        ratio: 1,
        maxWeightKey: "imageMaxSize200kb",
    },
    zoneThumb: {
        ratioLabel: "1:1",
        ratio: 1,
        maxWeightKey: "imageMaxSize200kb",
    },
    floorPlan: {
        ratioLabel: null,
        ratio: null,
        maxWeightKey: "imageMaxSize4Mb",
    },
    fullscreen: {
        ratioLabel: null,
        ratio: null,
        maxWeightKey: "imageMaxSize200kb",
    },
    avatar: {
        ratioLabel: "1:1",
        ratio: 1,
        maxWeightKey: "imageMaxSize300kb",
    },
    progression: {
        ratioLabel: "3:2",
        ratio: 3 / 2,
        maxWeightKey: "imageMaxSize1Mb",
    },
    socialShare: {
        ratioLabel: "3:4",
        ratio: 3 / 4,
        maxWeightKey: "imageMaxSize300kb",
    },
    /** Hub story detail banner ≈ 3:2 (list 1:1 vignette is secondary) */
    hubStory: {
        ratioLabel: "3:2",
        ratio: 3 / 2,
        maxWeightKey: "imageMaxSize200kb",
    },
    /** Hub folder hero ≈ 16:9 */
    hubFolderCover: {
        ratioLabel: "16:9",
        ratio: 16 / 9,
        maxWeightKey: "imageMaxSize200kb",
        ratioTolerance: 0.2,
    },
    hubLogo: {
        ratioLabel: null,
        ratio: null,
        maxWeightKey: "imageMaxSize200kb",
    },
} as const satisfies Record<string, ImageSpec>;

export type ImageSpecKey = keyof typeof IMAGE_SPECS;
